import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import DashboardLayout from '../Layouts/DashboardLayout';
import { useVisionPostGenerator } from '../../hooks/useVisionPostGenerator';
import logger from '../../utils/logger';

const initialContext = {
  business_name: '',
  category: '',
  city: '',
  neighborhood: '',
  offer_or_event: '',
};

const VisionPostGeneratorPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [context, setContext] = useState(initialContext);
  const [localError, setLocalError] = useState('');
  const [copied, setCopied] = useState(false);

  const {
    result,
    error,
    isGenerating,
    generateFromImage,
    reset: resetGenerator,
  } = useVisionPostGenerator();

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setLocalError('Please select a valid image file (JPG, PNG, or WebP).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setLocalError('Image must be smaller than 10MB.');
      return;
    }

    setLocalError('');
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleGenerate = async () => {
    if (!selectedFile) {
      setLocalError('Upload an image before generating a post.');
      return;
    }

    try {
      setCopied(false);
      await generateFromImage(selectedFile, context);
    } catch (err) {
      logger.error('[VisionPostGenerator] Generate button error:', err);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setContext(initialContext);
    setCopied(false);
    setLocalError('');
    resetGenerator();
  };

  const handleCopy = () => {
    if (!result) return;
    const hashtagsLine = result.hashtags?.length ? `\n\n${result.hashtags.join(' ')}` : '';
    const text = `${result.post}${hashtagsLine}`;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      })
      .catch((err) => {
        logger.error('[VisionPostGenerator] Failed to copy content:', err);
      });
  };

  const renderPreview = () => {
    if (!selectedFile || !previewUrl) {
      return (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed #cbd5f5',
            bgcolor: '#f8fafc',
            px: 3,
            py: 6,
            textAlign: 'center',
            color: '#64748b',
          }}
        >
          <Typography variant="subtitle1">Your uploaded image will appear here.</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Choose a clear photo of your storefront, service, or product to generate the most accurate caption.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={previewUrl}
          alt={selectedFile.name}
          sx={{ width: '100%', height: 320, objectFit: 'cover' }}
        />
        <Box sx={{ p: 2, backgroundColor: '#0f172a', color: '#fff' }}>
          <Typography variant="body2" fontWeight={600} noWrap title={selectedFile.name}>
            {selectedFile.name}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB · {selectedFile.type || 'image'}
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <DashboardLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} color="#0f172a">
          Vision-powered Google Business Post Lab
        </Typography>
        <Typography variant="body1" color="#475569" sx={{ mt: 1 }}>
          Upload any business photo and get a CTA-ready post, local SEO keywords, and hashtags that match what’s
          actually visible.
        </Typography>
      </Box>

      <Stack spacing={4}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
          <Typography variant="h6" fontWeight={600} color="#0f172a" gutterBottom>
            Step 1 · Provide business context
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              label="Business Name"
              value={context.business_name}
              onChange={(e) => setContext(prev => ({ ...prev, business_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Category (e.g. Property Consultant)"
              value={context.category}
              onChange={(e) => setContext(prev => ({ ...prev, category: e.target.value }))}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="City"
              value={context.city}
              onChange={(e) => setContext(prev => ({ ...prev, city: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Neighborhood / Landmark"
              value={context.neighborhood}
              onChange={(e) => setContext(prev => ({ ...prev, neighborhood: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Offer or Event"
              value={context.offer_or_event}
              onChange={(e) => setContext(prev => ({ ...prev, offer_or_event: e.target.value }))}
              fullWidth
            />
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
          <Typography variant="h6" fontWeight={600} color="#0f172a" gutterBottom>
            Step 2 · Upload a photo
          </Typography>
          <Stack spacing={3}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={isGenerating}
              sx={{
                borderStyle: 'dashed',
                py: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {selectedFile ? 'Replace Image' : 'Select Image'}
              <input type="file" hidden accept="image/*" onChange={handleFileChange} />
            </Button>
            {localError && (
              <Typography variant="body2" color="error">
                {localError}
              </Typography>
            )}
            {renderPreview()}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#0f172a">
                Step 3 · Generate your post
              </Typography>
              <Typography variant="body2" color="#64748b">
                We’ll analyze the actual photo and craft a ready-to-post summary, keywords, and hashtags.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                disabled={isGenerating}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {isGenerating ? 'Generating...' : 'Generate Post'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#0f172a">
                Result
              </Typography>
              <Typography variant="body2" color="#64748b">
                {isGenerating
                  ? 'Analyzing your image and crafting a tailored Google Business post...'
                  : 'Copy the post or adjust the text before publishing.'}
              </Typography>
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy post & hashtags'}>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleCopy}
                  disabled={!result || isGenerating}
                >
                  {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {isGenerating && (
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="#64748b">
                Crafting a summary that matches the actual photo...
              </Typography>
            </Stack>
          )}

          {!isGenerating && (error || localError) && (
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #fecaca',
                bgcolor: '#fef2f2',
                p: 3,
                borderRadius: 3,
              }}
            >
              <Typography variant="subtitle1" color="#b91c1c" fontWeight={600}>
                Something went wrong
              </Typography>
              <Typography variant="body2" color="#b91c1c">
                {error || localError}
              </Typography>
            </Paper>
          )}

          {!isGenerating && !error && !result && !localError && (
            <Typography variant="body2" color="#94a3b8">
              Upload an image and click “Generate Post” to see the AI result here.
            </Typography>
          )}

          {!isGenerating && result && (
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#f8fafc',
                }}
              >
                <Typography variant="body1" color="#0f172a" sx={{ whiteSpace: 'pre-line' }}>
                  {result.post}
                </Typography>
              </Paper>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="#475569" gutterBottom>
                    Keywords
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {result.keywords?.length ? (
                      result.keywords.map(keyword => (
                        <Chip key={keyword} label={keyword} sx={{ mb: 1 }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="#94a3b8">
                        Not provided by AI.
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="#475569" gutterBottom>
                    Hashtags
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {result.hashtags?.length ? (
                      result.hashtags.map(tag => (
                        <Chip key={tag} label={tag.startsWith('#') ? tag : `#${tag}`} sx={{ mb: 1 }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="#94a3b8">
                        Not provided by AI.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Stack>
    </DashboardLayout>
  );
};

export default VisionPostGeneratorPage;
